/**
 * Shared data model for the vectorization pipeline.
 *
 * Every stage — CPU preprocessing, the GPU optimizer, and SVG export — speaks
 * these types, so they are deliberately plain: typed arrays and structs that
 * survive `postMessage` structured cloning without a conversion step.
 */

/** A binary image. `data[y * width + x]` is 1 for ink, 0 for background. */
export interface BinaryMask {
  width: number
  height: number
  data: Uint8Array
}

/** A single-channel f32 image, used for distance transforms and renders. */
export interface ScalarField {
  width: number
  height: number
  data: Float32Array
}

/**
 * A polyline of skeleton pixels, in traversal order.
 * Flattened as `[x0, y0, x1, y1, …]` in working-resolution pixel coordinates.
 */
export interface PixelPath {
  points: Float32Array
  /** True when the last point coincides with the first. */
  closed: boolean
}

/**
 * A stroke: a cubic Bézier centerline plus a width profile.
 *
 * `points` holds `3n + 1` control points for `n` segments, flattened as
 * `[x0, y0, x1, y1, …]`, so segment `k` uses points `3k … 3k + 3`. Coordinates
 * are in working-resolution pixels.
 *
 * `width` holds the coefficients `a_0 … a_d` of the *half*-width (radius)
 * polynomial `w(T) = Σ a_k T^k`, where `T ∈ [0, 1]` is normalized arc length.
 * A degree-0 polynomial is a constant-width stroke.
 */
export interface Stroke {
  points: Float32Array
  width: Float32Array
  closed: boolean
}

export function segmentCount(stroke: Stroke): number {
  return (stroke.points.length / 2 - 1) / 3
}

/** Evaluates the half-width polynomial at normalized arc length `t`. */
export function widthAt(coefficients: ArrayLike<number>, t: number): number {
  let value = 0
  for (let k = coefficients.length - 1; k >= 0; k--) {
    value = value * t + coefficients[k]
  }
  return value
}

// ---------------------------------------------------------------------------
// Parameters
// ---------------------------------------------------------------------------

export type QualityPreset = 'fast' | 'default' | 'high'

export const ITERATIONS_BY_PRESET: Record<QualityPreset, number> = {
  fast: 100,
  default: 200,
  high: 400,
}

export type WidthMode = 'uniform' | 'variable'

export interface PipelineParams {
  /** Longest side of the working resolution, in pixels. */
  workingResolution: number
  /** White-on-black art: flip the mask after binarization. */
  invert: boolean
  /**
   * Correct the polarity automatically when the mask comes out mostly ink.
   * Line art is sparse, so a mostly-ink mask means the polarity was wrong.
   */
  autoInvert: boolean
  /** 0–255 threshold, or `null` for Otsu's automatic threshold. */
  threshold: number | null
  /** Connected components smaller than this (in pixels) are removed. */
  despeckle: number
  /** Skeleton paths shorter than this (in pixels) are discarded. */
  minStrokeLength: number
  /** Maximum allowed deviation, in pixels, when fitting Béziers to a path. */
  fitError: number
  widthMode: WidthMode
  /** Degree of the width polynomial when `widthMode` is `variable`. */
  widthDegree: number
  /** Run the differentiable optimizer after the classic fit. */
  optimize: boolean
  quality: QualityPreset
  /** Overrides the preset's iteration count when set. */
  iterations: number | null
  /** Maintenance passes during optimization. */
  prune: boolean
  simplify: boolean
  densify: boolean
  /** G¹ continuity weight; 0 disables the term. */
  smoothnessWeight: number
  /** Curvature-cap weight; 0 disables the term. */
  curvatureWeight: number
  /** Use the lazily-loaded neural models for junction affinities. */
  enhancedInit: boolean
  /** Seeds every stochastic step so runs are reproducible. */
  seed: number
}

export const DEFAULT_PARAMS: PipelineParams = {
  workingResolution: 768,
  invert: false,
  autoInvert: true,
  threshold: null,
  despeckle: 12,
  minStrokeLength: 6,
  fitError: 1.5,
  widthMode: 'variable',
  widthDegree: 3,
  optimize: true,
  quality: 'default',
  iterations: null,
  prune: true,
  simplify: true,
  densify: true,
  smoothnessWeight: 0,
  curvatureWeight: 0,
  enhancedInit: false,
  seed: 1,
}

export function iterationCount(params: PipelineParams): number {
  return params.iterations ?? ITERATIONS_BY_PRESET[params.quality]
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface VectorizationResult {
  strokes: Stroke[]
  /** Working resolution the strokes are expressed in. */
  width: number
  height: number
  /** Source image dimensions, used for the exported `viewBox`. */
  sourceWidth: number
  sourceHeight: number
  /** Mean squared error of the final render against the binarized input. */
  loss: number
  iterations: number
  params: PipelineParams
  /** Wall-clock milliseconds per stage, for the diagnostics panel. */
  timings: Record<string, number>
  /** Non-fatal problems worth telling the user about, e.g. GPU limits hit. */
  warnings?: string[]
}

// ---------------------------------------------------------------------------
// Worker protocol
// ---------------------------------------------------------------------------

export type PipelineStage =
  | 'preprocess'
  | 'skeleton'
  | 'graph'
  | 'fit'
  | 'optimize'
  | 'export'
  | 'done'

export const STAGE_LABELS: Record<PipelineStage, string> = {
  preprocess: 'Preparing image…',
  skeleton: 'Skeletonizing…',
  graph: 'Tracing stroke paths…',
  fit: 'Fitting curves…',
  optimize: 'Optimizing…',
  export: 'Building SVG…',
  done: 'Done',
}

export interface StageProgress {
  type: 'stage'
  stage: PipelineStage
  /** 0–1 within the stage; omitted for instantaneous stages. */
  progress?: number
  detail?: string
}

export interface IterationProgress {
  type: 'iteration'
  iteration: number
  total: number
  loss: number
  strokeCount: number
}

/** A render of the current state, streamed to the UI as a live preview. */
export interface PreviewFrame {
  type: 'preview'
  width: number
  height: number
  /** Grayscale coverage, 0 = white paper, 255 = full ink. */
  pixels: Uint8ClampedArray
  iteration: number
}

export interface PipelineDone {
  type: 'done'
  result: VectorizationResult
}

export interface PipelineFailed {
  type: 'error'
  message: string
  /** Set when the failure is a lost/exhausted GPU device. */
  recoverable: boolean
}

export interface PipelineCancelled {
  type: 'cancelled'
}

export type PipelineEvent =
  | StageProgress
  | IterationProgress
  | PreviewFrame
  | PipelineDone
  | PipelineFailed
  | PipelineCancelled

/**
 * A region of the source image, in source pixels.
 *
 * Held separately from `PipelineParams` because it belongs to the image rather
 * than to the conversion settings: swapping the image discards it, changing the
 * quality preset does not.
 */
export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

export interface StartRequest {
  type: 'start'
  id: number
  image: ImageBitmap | ImageData
  params: PipelineParams
  /** Convert only this region; omitted means the whole image. */
  crop?: CropRect
}

export interface CancelRequest {
  type: 'cancel'
  id: number
}

export type PipelineRequest = StartRequest | CancelRequest

// ---------------------------------------------------------------------------
// Export options
// ---------------------------------------------------------------------------

export type SvgMode = 'centerline' | 'outline'

export interface SvgOptions {
  mode: SvgMode
  /** Give every stroke a distinct hue, for inspecting the segmentation. */
  colorize: boolean
  /** Round coordinates to this many decimals. */
  precision: number
  /** Emit a comment recording the parameters used. */
  includeMetadata: boolean
}

export const DEFAULT_SVG_OPTIONS: SvgOptions = {
  mode: 'centerline',
  colorize: false,
  precision: 2,
  includeMetadata: true,
}
